import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ProfileTabs } from "@/components/profile/ProfileTabs";
import { toast } from "sonner";
import { useUser } from "@/hooks/use-user";
import { Navigation } from "@/components/Navigation";
import { MobileNavigation } from "@/components/MobileNavigation";

// Database related imports and interfaces
import { Database } from "@/integrations/supabase/types";

type SocialMediaPlatform = "instagram" | "twitter" | "facebook" | "youtube" | "website";
interface SocialMediaLink {
  platform: SocialMediaPlatform;
  url: string;
}

interface Profile {
  id: string;
  username?: string;
  avatar_url?: string;
  banner_url?: string;
  website?: string;
  location?: string;
  bio?: string;
  is_onboarded?: boolean;
  social_links?: SocialMediaLink[];
  primary_skill?: string;
}

const Profile = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState<Profile | null>(null);
  const { user: currentUser, refreshUser } = useUser();
  const [isFollowing, setIsFollowing] = useState(false);
  const [userPosts, setUserPosts] = useState<any[]>([]);
  const [userShares, setUserShares] = useState<any[]>([]);
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [galleryItems, setGalleryItems] = useState<any[]>([]);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Iniciar a sidebar aberta (false significa não colapsada, ou seja, aberta)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Refs for file inputs
  const avatarFileRef = useRef<HTMLInputElement>(null);
  const bannerFileRef = useRef<HTMLInputElement>(null);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Determine if we're using username or ID
      const isUUID = username && 
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(username);
      
      let query = supabase.from("profiles").select("*");
      
      if (isUUID) {
        query = query.eq("id", username);
      } else if (username === 'me') {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) {
          query = query.eq("id", session.user.id);
        } else {
          navigate("/auth");
          return;
        }
      } else {
        query = query.eq("username", username);
      }
      
      // Use maybeSingle instead of single to handle case when profile doesn't exist
      const { data: profileData, error } = await query.maybeSingle();

      if (error) {
        console.error("Error fetching profile:", error);
        toast.error("Erro ao carregar perfil");
        setError("Erro ao carregar perfil");
        return;
      }

      if (!profileData) {
        setError("Perfil não encontrado");
        return;
      }

      // Type conversion for social_links if needed
      let socialLinks: SocialMediaLink[] = [];
      if (profileData.social_links) {
        try {
          // If it's a string, parse it, if it's already an object use it
          const rawLinks = typeof profileData.social_links === 'string' 
            ? JSON.parse(profileData.social_links) 
            : profileData.social_links;
          
          // Ensure it's an array and convert to correct type
          if (Array.isArray(rawLinks)) {
            socialLinks = rawLinks.map(link => ({
              platform: link.platform as SocialMediaPlatform,
              url: link.url
            }));
          }
        } catch (e) {
          console.error("Error parsing social_links:", e);
          socialLinks = [];
        }
      }
      
      // Create a new profile object with proper typing
      const typedProfile: Profile = {
        ...profileData,
        social_links: socialLinks
      };
      
      setProfile(typedProfile);

      // Fetch posts by the user
      const { data: posts, error: postsError } = await supabase
        .from("posts")
        .select("*")
        .eq("user_id", profileData.id)
        .order('created_at', { ascending: false });

      if (postsError) {
        console.error("Error fetching posts:", postsError);
        toast.error("Erro ao carregar postagens");
      } else if (posts) {
        setUserPosts(posts);
      }

      // Fetch shares by the user
      const { data: shares, error: sharesError } = await supabase
        .from("shares")
        .select("*, posts(*)")
        .eq("user_id", profileData.id)
        .order('created_at', { ascending: false });

      if (sharesError) {
        console.error("Error fetching shares:", sharesError);
        toast.error("Erro ao carregar itens compartilhados");
      } else if (shares) {
        setUserShares(shares);
      }
      
      // Fetch user portfolio items for gallery
      const { data: portfolio, error: portfolioError } = await supabase
        .from("user_portfolio")
        .select("*")
        .eq("user_id", profileData.id)
        .order('display_order', { ascending: true });
        
      if (portfolioError) {
        console.error("Error fetching portfolio:", portfolioError);
      } else if (portfolio) {
        // Transform portfolio items into gallery format
        const items = portfolio.map((item, index) => ({
          id: item.id,
          type: "image", // Assuming all are images for now
          title: item.title,
          desc: item.description || "",
          url: item.image_url,
          span: index % 3 === 0 
            ? "md:col-span-2 md:row-span-2" 
            : "md:col-span-1 md:row-span-2"
        }));
        
        setGalleryItems(items);
      }
      
      await fetchFollowCounts(profileData.id);
      
      if (currentUser?.id) {
        await checkFollowStatus(profileData.id);
      }
    } catch (error) {
      console.error("Error in data fetching:", error);
      toast.error("Erro ao carregar dados do perfil");
      setError("Erro ao carregar dados do perfil");
    } finally {
      setLoading(false);
    }
  };

  const checkFollowStatus = async (profileId: string) => {
    if (!currentUser?.id || !profileId) return;

    try {
      const { data, error } = await supabase
        .from("follows")
        .select("*")
        .eq("follower_id", currentUser.id)
        .eq("following_id", profileId)
        .maybeSingle();

      if (error) {
        console.error("Error checking follow status:", error);
        return;
      }

      setIsFollowing(!!data);
    } catch (error) {
      console.error("Error checking follow status:", error);
    }
  };

  const fetchFollowCounts = async (profileId: string) => {
    if (!profileId) return;

    try {
      // Get followers count
      const { data: followers, error: followersError } = await supabase
        .from("follows")
        .select("*", { count: "exact" })
        .eq("following_id", profileId);

      if (followersError) {
        console.error("Error fetching followers count:", followersError);
      } else {
        setFollowersCount(followers.length);
      }

      // Get following count
      const { data: following, error: followingError } = await supabase
        .from("follows")
        .select("*", { count: "exact" })
        .eq("follower_id", profileId);

      if (followingError) {
        console.error("Error fetching following count:", followingError);
      } else {
        setFollowingCount(following.length);
      }
    } catch (error) {
      console.error("Error fetching follow counts:", error);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, [username, navigate]);

  const handleFollowToggle = async () => {
    if (!currentUser) {
      navigate("/auth");
      return;
    }

    if (!profile?.id) return;

    try {
      if (isFollowing) {
        // Unfollow user
        const { error } = await supabase
          .from("follows")
          .delete()
          .eq("follower_id", currentUser.id)
          .eq("following_id", profile.id);

        if (error) {
          console.error("Error unfollowing user:", error);
          toast.error("Falha ao deixar de seguir usuário");
          return;
        }

        setIsFollowing(false);
        setFollowersCount(prev => prev - 1);
        toast.success("Deixou de seguir o usuário com sucesso");
      } else {
        // Follow user
        const { error } = await supabase
          .from("follows")
          .insert({
            follower_id: currentUser.id,
            following_id: profile.id
          });

        if (error) {
          console.error("Error following user:", error);
          toast.error("Falha ao seguir usuário");
          return;
        }

        setIsFollowing(true);
        setFollowersCount(prev => prev + 1);
        toast.success("Seguindo usuário com sucesso");
      }
    } catch (error) {
      console.error("Error toggling follow status:", error);
      toast.error("Ocorreu um erro");
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const handleProfileUpdate = () => {
    fetchProfileData();
    refreshUser();
  };

  const handleBannerFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0 || !currentUser) {
      return;
    }
    
    const file = e.target.files[0];
    const fileExt = file.name.split('.').pop();
    const filePath = `${currentUser.id}/banner.${fileExt}`;
    
    try {
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });
        
      if (uploadError) {
        throw uploadError;
      }
      
      const { data: publicURL } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
        
      if (publicURL) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ banner_url: publicURL.publicUrl })
          .eq('id', currentUser.id);
          
        if (updateError) {
          throw updateError;
        }
        
        toast.success("Banner atualizado com sucesso!");
        handleProfileUpdate();
      }
    } catch (error) {
      console.error('Erro ao fazer upload do banner:', error);
      toast.error("Erro ao fazer upload do banner.");
    }
  };
  
  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0 || !currentUser) {
      return;
    }
    
    const file = e.target.files[0];
    const fileExt = file.name.split('.').pop();
    const filePath = `${currentUser.id}/avatar.${fileExt}`;
    
    try {
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });
        
      if (uploadError) {
        throw uploadError;
      }
      
      const { data: publicURL } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
        
      if (publicURL) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ avatar_url: publicURL.publicUrl })
          .eq('id', currentUser.id);
          
        if (updateError) {
          throw updateError;
        }
        
        toast.success("Avatar atualizado com sucesso!");
        handleProfileUpdate();
      }
    } catch (error) {
      console.error('Erro ao fazer upload do avatar:', error);
      toast.error("Erro ao fazer upload do avatar.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h1 className="text-2xl font-bold mb-4">Perfil não encontrado</h1>
        <p className="text-muted-foreground mb-4">
          {error || "O perfil que você está procurando não existe ou foi removido."}
        </p>
        <button 
          onClick={() => navigate('/')} 
          className="px-4 py-2 bg-primary text-white rounded-md"
        >
          Voltar à Página Inicial
        </button>
      </div>
    );
  }

  const isOwnProfile = currentUser?.id === profile.id;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="flex-1">
        <ProfileHeader
          profile={profile}
          currentUser={currentUser?.id || null}
          isFollowing={isFollowing}
          followersCount={followersCount}
          followingCount={followingCount}
          isMobile={isMobile}
          onProfileUpdate={handleProfileUpdate}
          onFollowToggle={handleFollowToggle}
          onLogout={handleLogout}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          handleBannerFileChange={handleBannerFileChange}
          handleAvatarFileChange={handleAvatarFileChange}
          bannerFileRef={bannerFileRef}
          avatarFileRef={avatarFileRef}
          isOwnProfile={isOwnProfile}
        />

        <div className={`container mx-auto px-4 ${isMobile ? 'mt-4' : 'mt-12'} transition-all duration-300 ${!sidebarCollapsed ? 'md:ml-64' : ''}`}>
          <ProfileTabs
            profile={profile}
            userPosts={userPosts}
            userShares={userShares}
            isMobile={isMobile}
            isOwnProfile={isOwnProfile}
            galleryItems={galleryItems}
          />
        </div>
      </div>
      <MobileNavigation />
    </div>
  );
};

export default Profile;
